/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: Shared OBC content
 * Reason: The whole content of the read-only bank account overview OBC (TM-1000002030), in two widgets:
 *         the „Basisdaten“ of one cr.CustomerBankAccount on top (data widget), the „Abrechnungskonten“ below
 *         (QL widget table, one row per assignment slice bm.CustomerBillingAccountBank that points at the bank
 *         account, with „Zugeordnet ab / bis“ and the slice state). Rendered by the full and the side view.
 *
 * ── WIDGETS ARE BUILT ONCE PER (bankAccountId, compact) (AU-9)
 *
 *   `initComponent()` builds both in `ngOnInit`; after that `ngOnChanges` rebuilds them ONLY when the pair
 *   actually differs from the one they were built for - never in a getter, never per change detection. A
 *   fresh `WidgetData` makes `<mvs-widget>` reload, so a needless rebuild would re-issue both requests. The
 *   builders live in `cr/widget/` (aliases, query, curated columns, display-only FIXED params) and return
 *   `null` without a usable id; this component then shows its no-object state instead of a widget.
 *
 * ── READ-ONLY (FD-3 / S-2)
 *
 *   `[disableUpdate]` bound to true on BOTH `<mvs-widget>` (template): it drops „Anlegen“, „Ändern“ and
 *   „Anzeigen“ from the widget menu (`mvs-widget.component.ts`, menu keys add/change/show). Together with
 *   the FIXED `formEdit` / `formCopy` / `allowDoubleClick = false` of the builders nothing here can write.
 *   There is no button of any kind in this component.
 *
 * ── THE THREE DERIVED CELLS (`#cust_`, H5)
 *
 *   Abrechnungskonto (name / „Konto #id“ + „(geschlossen)“), Zugeordnet bis („unbefristet“) and Status
 *   (Aktuell / Geplant ab … / Vergangen). Their `fieldId`s come from `CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS`,
 *   the same constant the builder writes into the layout. The values are folded by the pure functions of
 *   `cr/logic/cr-bank-account-billing-account-cell.ts` against today's date, fixed at build time (E-2). The
 *   status tag is coloured ONLY through `styleClass` with PrimeFlex tokens (`crBankAccountSliceStateStyleClass`):
 *   the global `.p-tag` override makes `[severity]` ineffective, and the colour utilities are `!important`,
 *   so no inline style is set either (AU-6).
 *
 * ── ROW CLICK (FD-7 / S-3)
 *
 *   The table reports the SLICE (`bm.CustomerBillingAccountBank`, its own id); the BILLING ACCOUNT id is read
 *   from the row data. A plain single click opens the billing account (`bm.CustomerBillingAccount`, its
 *   existing account view) in the LEFT drawer, so the bank account stays visible on the right; Ctrl/Cmd opens
 *   it in a new tab. Every other event (right click, middle click, an event without a type) and a row without
 *   an account id do nothing. The account is addressed by its object-type STRING - no TypeScript import from
 *   feature-bm (AU-8 / I11).
 *
 * ── LAYOUT: STACKED IN BOTH VARIANTS - DEVIATION FROM REQUIREMENT §5.2 (review M-7)
 *
 *   §5.2 proposed a PrimeFlex `grid` for the FULL view: base data `col-12 lg:col-4`, table `col-12 lg:col-8`.
 *   It is NOT built, because the base data would become unreadable in that cell:
 *   <ul>
 *     <li>the builder fixes `columns: 2` for the full variant (`buildCrBankAccountBaseDataWidget`), and the data
 *         widget turns that into a plain `col-6` per field - no responsive class outside its expert mode
 *         (`MvsWidgetDataComponent.calculateScreenColumns`), label above value;</li>
 *     <li>inside `lg:col-4` that leaves one field roughly 130-190 px (lg starts at a 992 px viewport, and the
 *         object frame takes its own navigation column): the full 22-character IBAN (Q-11c, unmasked)
 *         breaks or overflows, and labels such as „Abweichender Kontoinhaber“ or „SEPA-Mandat vorhanden“
 *         wrap over two lines - 17 fields as a cramped two-column strip next to a six-column table.</li>
 *   </ul>
 *   The two-column card is designed for a WIDE container, so the full view stacks it above the table, as the
 *   cp precedent does. Switching to the grid would first need `columns: 1` for the full variant in the widget
 *   builder (a widget-layer change, outside this component). The compact (side) variant stacks anyway.
 *
 * No `p-tabs` and no template reference named `#content` (PrimeNG tabpanel hijack).
 *
 * @ticket TM-1000002030
 */
import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {ObjectIdentifierData} from 'features/core/shared/basic/object-identifier-data';
import {MvsObjectNavigationService} from 'features/core/shared/navigation/mvs-object-navigation.service';
import {MvsObjectNavigationEntry} from 'features/core/shared/navigation/mvs-object-navigation-entry';
import {MvsObjectNavigationActionEnum} from 'features/core/shared/navigation/mvs-object-navigation-action-enum';
import {buildCrBankAccountBaseDataWidget} from '../../../../../widget/cr-bank-account-base-data.widget';
import {
    buildCrBankAccountBillingAccountsWidget,
    CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS
} from '../../../../../widget/cr-bank-account-billing-accounts.widget';
import {
    crBankAccountSliceAccountClosed,
    crBankAccountSliceAccountLabel,
    crBankAccountSliceBillingAccountId,
    crBankAccountSliceEndLabel,
    crBankAccountSliceSourceRow,
    crBankAccountSliceStateLabel,
    crBankAccountSliceStateStyleClass,
    crBankAccountSliceView
} from '../../../../../logic/cr-bank-account-billing-account-cell';
import type {CrBankAccountUsageRow} from '../../../../../logic/cr-bank-account-usage-row';
import {crBankChangeToIsoDate} from '../../../../../logic/cr-bank-change-valid-from';

/**
 * Object type a row click opens - the billing account behind the slice, as a STRING (no feature-bm import).
 *
 * @ticket TM-1000002030
 */
export const CR_BANK_ACCOUNT_OVERVIEW_BILLING_ACCOUNT_OBJECT_TYPE = 'bm.CustomerBillingAccount';

/** Table event type of a plain left click (`MvsWidgetTableComponent.onRowSelected`). */
const EVENT_SINGLE_CLICK = 'singleClick';

/** Table event type of a left click with Ctrl (Windows/Linux) or Cmd (macOS) held. */
const EVENT_SINGLE_CLICK_CTRL = 'singleClickCtrl';

@Component({
    selector: 'cr-customer-bank-account-overview',
    templateUrl: './cr-customer-bank-account-overview.component.html',
    styleUrls: ['./cr-customer-bank-account-overview.component.scss'],
    standalone: false
})
export class CrCustomerBankAccountOverviewComponent implements OnInit, OnChanges {

    /** Id of the `cr.CustomerBankAccount`; `null`/`0` shows the no-object state. */
    @Input() bankAccountId: number;

    /** Narrow variant for the drawer: one-column base data, four table columns, paging 10, side aliases. */
    @Input() compact: boolean = false;

    initialized: boolean = false;

    /** „Basisdaten“ - data widget, `uiComponent: 'data'`, a reading surface. */
    baseDataWidget: WidgetData | null = null;

    /** „Abrechnungskonten“ - curated QL table over the slices of this bank account. */
    billingAccountsWidget: WidgetData | null = null;

    /** `fieldId` / `label` of the three `#cust_` cells, shared with the widget builder. */
    readonly columns = CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS;

    /** The pair the current widgets were built for (AU-9). */
    private builtBankAccountId: number | null | undefined;
    private builtCompact: boolean | undefined;

    /** Key date of the slice state, local calendar day, fixed per build (E-2). */
    private todayIso: string;

    /**
     * Folded view per source row. The cells are re-evaluated on every change detection; the row objects of a
     * loaded page are stable, so each row is folded once. Reset on every rebuild.
     */
    private sliceViews = new WeakMap<object, CrBankAccountUsageRow | null>();

    constructor(private navigationService: MvsObjectNavigationService) {
    }

    /**
     * Initialization.
     */
    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize the component.
     */
    initComponent(): void {
        this.refreshComponent();
    }

    /**
     * Build both widgets for the current (bankAccountId, compact) pair - and do nothing when they were
     * already built for exactly that pair (AU-9).
     */
    refreshComponent(): void {

        const bankAccountId: number | null | undefined = this.bankAccountId;
        const compact: boolean = this.compact === true;

        if (this.initialized && bankAccountId === this.builtBankAccountId && compact === this.builtCompact) {
            return;
        }

        this.todayIso = crBankChangeToIsoDate(new Date());
        this.sliceViews = new WeakMap<object, CrBankAccountUsageRow | null>();

        this.baseDataWidget = buildCrBankAccountBaseDataWidget(bankAccountId, compact);
        this.billingAccountsWidget = buildCrBankAccountBillingAccountsWidget(bankAccountId, compact);

        this.builtBankAccountId = bankAccountId;
        this.builtCompact = compact;
        this.initialized = true;
    }

    /**
     * Rebuild only on a changed bank account or variant; the pair check in {@link refreshComponent} keeps a
     * re-notification of the same values from reloading the widgets.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['bankAccountId'] || changes['compact']) {
            this.refreshComponent();
        }
    }

    // ------------------------------------------------------------------ //
    // #cust_ cells - `object` is the table's template context `{row, dto}`
    // ------------------------------------------------------------------ //

    /** The folded slice behind a cell or an event, `null` for an unusable row. */
    sliceView(object: unknown): CrBankAccountUsageRow | null {
        const row: Record<string, any> | null = crBankAccountSliceSourceRow(object);
        if (row == null) {
            return null;
        }
        if (this.sliceViews.has(row)) {
            return this.sliceViews.get(row) ?? null;
        }
        const view: CrBankAccountUsageRow | null = crBankAccountSliceView(row, this.todayIso);
        this.sliceViews.set(row, view);
        return view;
    }

    /** „Abrechnungskonto“: the account name, otherwise „Konto #id“. */
    accountLabel(object: unknown): string {
        return crBankAccountSliceAccountLabel(this.sliceView(object));
    }

    /** Whether the cell adds „(geschlossen)“. */
    accountClosed(object: unknown): boolean {
        return crBankAccountSliceAccountClosed(this.sliceView(object));
    }

    /** „Zugeordnet bis“: dd.MM.yyyy, or „unbefristet“ for the open end. */
    endLabel(object: unknown): string {
        return crBankAccountSliceEndLabel(this.sliceView(object));
    }

    /** „Status“: Aktuell / Geplant ab dd.MM.yyyy / Vergangen; empty for an unusable row. */
    stateLabel(object: unknown): string {
        return crBankAccountSliceStateLabel(this.sliceView(object));
    }

    /** PrimeFlex colour tokens of the status tag - the ONLY way its colour is set (AU-6). */
    stateStyleClass(object: unknown): string {
        return crBankAccountSliceStateStyleClass(this.sliceView(object)?.state);
    }

    // ------------------------------------------------------------------ //
    // Row click (FD-7 / S-3)
    // ------------------------------------------------------------------ //

    /**
     * `(onObjectSelect)` of the table. Opens the billing account behind the clicked slice:
     * <ul>
     *   <li>`singleClick` - LEFT drawer, `uiMode 'side'`, numeric action `any`;</li>
     *   <li>`singleClickCtrl` - a new browser tab (Ctrl on Windows/Linux, Cmd on macOS);</li>
     *   <li>anything else, or no billing account id in the row - nothing.</li>
     * </ul>
     */
    handleBillingAccountSelect(event: ObjectIdentifierData | ObjectIdentifier | null | undefined): void {

        const eventType: string | undefined = (event as ObjectIdentifierData)?.event;

        if (eventType !== EVENT_SINGLE_CLICK && eventType !== EVENT_SINGLE_CLICK_CTRL) {
            return;
        }

        const row: Record<string, any> | null = crBankAccountSliceSourceRow((event as ObjectIdentifierData).data);
        const billingAccountId: number | null = crBankAccountSliceBillingAccountId(row);

        if (!billingAccountId) {
            return;
        }

        const view: CrBankAccountUsageRow | null = this.sliceView(row);
        const label: string | null = view ? crBankAccountSliceAccountLabel(view) : null;

        if (eventType === EVENT_SINGLE_CLICK_CTRL) {
            this.navigationService.handleObjectNavigation(
                new ObjectIdentifier(CR_BANK_ACCOUNT_OVERVIEW_BILLING_ACCOUNT_OBJECT_TYPE, billingAccountId),
                null,
                {openNewTab: true, openNewTabFocus: true},
                label);
            return;
        }

        this.navigationService.navigateTo(
            MvsObjectNavigationEntry.createNavigationEntry(
                CR_BANK_ACCOUNT_OVERVIEW_BILLING_ACCOUNT_OBJECT_TYPE,
                billingAccountId,
                'object',
                label,
                null,
                null,
                MvsObjectNavigationActionEnum.any,
                'side'),
            'left');
    }

}
